import { DeleteWorkOrderModal } from "@/shared/work-orders-modals/components/modal-delete-work-order";
import { EditWorkOrderModal } from "@/shared/work-orders-modals/components/modal-edit-work-order";
import {
	AssetTreePicker,
	ErrorModal,
	getMomentTz,
	useToken,
	useUserContext,
} from "@dexteel/mesf-core";
import { Button, IconButton, Paper, Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import AddBoxIcon from "@material-ui/icons/AddBox";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTaskListContext } from "../context/taskListContext";
import { Filters } from "../filters";
import { useGetDataFromAPI } from "../hooks/useGetDataFromAPI";
import { useInitializeTaskList } from "../hooks/useInitializeTaskList";
import { useSearchData } from "../hooks/useSearchData";
import { UpdateTaskListPriority } from "../repositorie/TaskListRepositorie";

import { useSearchWorkOrderAttachments } from "@/shared/work-orders-modals/components/hooks/useSearchWOAttachaments";
import { CloseWorkOrderModalWithAttachments } from "@/shared/work-orders-modals/components/modal-close-work-order";
import { ViewerAttachmentsLinked } from "@/shared/work-orders-modals/components/viewer-attachments/viewer-attachments-linked-to-wo";
import {
	type ResourceType,
	TableWorkOrdesResoures,
} from "@/shared/work-orders-modals/components/work-order-resources/table-work-ordes-resoures";
import { INITIAL_VALUES_MODAL } from "@/shared/work-orders-modals/models/ModalInitialValue";
import type { ModalWorkOrder } from "@/shared/work-orders-modals/models/ModalWorkOrder";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import type {
	CellEditingStoppedEvent,
	GetRowIdFunc,
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
	RowDoubleClickedEvent,
	RowDragEndEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import type {
	ColDef,
	GetContextMenuItemsParams,
	GetRowIdParams,
	MenuItemDef,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import CustomLoadingOverlay from "../../../components/loaders/LazyLoading/LazyLoading";
import { AssetNameColumnColumn } from "../components/assetName-column";

type onMenuOptionSelectedType =
	| "EDIT"
	| "EDIT-COMMENTS"
	| "SEE-COMMENTS"
	| "CLOSE-ORDER"
	| "ADD-ORDER"
	| "DELETE-ORDER";

const moment = getMomentTz();

export const TaskListByAreas = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const [newMode, setNewMode] = useState(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");
	const [isTaskListLoading, setIsTaskListLoading] = useState<boolean>(true);
	const [taskListError, setTaskListError] = useState<string>("");
	const [insertTaskError, setInsertTaskError] = useState<string>("");
	const [updatePriorityError, setUpdatePriorityError] = useState<string>("");
	const gridStyle = useMemo(() => ({ height: "90%", width: "100%" }), []);
	const [areaApi, setAreaApi] = useState<{ [key: number]: GridApi } | null>(
		null,
	);
	const { getUserName } = useToken();

	const {
		state: {
			openWorkOrderEditModal,
			openCloseWorkOrderModal,
			openDeleteWorkOrderModal,
			downHours,
			selectedRow,
			assetSearchId,
			assetSearchName,
			assetCodes,
			rootAssetsDataTables,
			openDialogLinkAttachmentToWo,
		},
		actions: {
			setOpenWorkOrderEditModal,
			setOpenCommentsModal,
			setOpenCloseWorkOrderModal,
			setOpenDeleteWorkOrderModal,
			setFirstTime,
			setSelectedRow,
			setOpenDialogLinkAttachmentToWO,
			setWorkOrderSelectedId,
			setWorkOrderSelectedName,
		},
	} = useTaskListContext();
	const [updateAttachmentsList, setUpdateAttachmentsList] = useState(true);

	const [attachmentsList, setAttachmentsList] = useState([]);
	const searchWorkOrderAttachments = useSearchWorkOrderAttachments({
		WorkOrderId: selectedRow?.WorkOrderId,
		setAttachmentsList,
		setIsLoading,
		setError,
	});

	const [openWorkOrderResources, setOpenWorkOrderResources] = useState(false);
	const [selectedResource, setSelectedResource] = useState<ResourceType | null>(
		null,
	);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: CellEditingStoppedEvent,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			mouseEvent: e.nativeEvent,
		});
	};

	const onMenuOptionSelected = (
		option: onMenuOptionSelectedType,
		row: ModalWorkOrder,
		areaAssetId: number | null = null,
	) => {
		if (!row) return;

		setSelectedRow(row);
		switch (option) {
			case "EDIT": {
				setOpenWorkOrderEditModal(true);
				return 0;
			}
			case "EDIT-COMMENTS": {
				setOpenCommentsModal(true);
				return 0;
			}
			case "SEE-COMMENTS": {
				setOpenCommentsModal(true);
				return 0;
			}
			case "CLOSE-ORDER": {
				setOpenCloseWorkOrderModal(true);
				return 0;
			}
			case "ADD-ORDER": {
				if (areaAssetId !== null) {
					addNewOrder(areaAssetId);
				}
				return 0;
			}
			case "DELETE-ORDER": {
				setOpenDeleteWorkOrderModal(true);
				return 0;
			}
			default:
		}
	};

	const openResourceModal = useCallback(
		(resource: ResourceType, rowData: any) => {
			if (!rowData) return;

			setSelectedRow(rowData);
			setSelectedResource(resource);
			setOpenWorkOrderResources(true);
		},
		[setSelectedRow],
	);

	const getRowId = useMemo<GetRowIdFunc>(() => {
		return (params: GetRowIdParams) => {
			return params.data?.id || "";
		};
	}, []);

	const onRowDragEnd = async (params: RowDragEndEvent) => {
		if (!params.node?.data?.WorkOrderId || !params.api) return;

		let workOrderIdBelow = null;

		if (params.overNode && params.overNode.data) {
			workOrderIdBelow = params.overNode.data.WorkOrderId;
		} else if (params.overIndex !== undefined && params.overIndex >= 0) {
			const targetRow = params.api.getDisplayedRowAtIndex(params.overIndex);
			if (targetRow && targetRow.data) {
				workOrderIdBelow = targetRow.data.WorkOrderId;
			}
		}

		try {
			const res = await UpdateTaskListPriority(
				params.node.data.WorkOrderId,
				workOrderIdBelow,
				null,
			);

			if (res.ok) {
				searchData();
			} else {
				setUpdatePriorityError(res.message);
			}
		} catch (error) {
			setUpdatePriorityError("Error updating priority");
		}
	};

	const onGridReadyForArea = (area: number, params: GridReadyEvent) => {
		if (!params.api) return;

		setAreaApi((prev) => ({ ...(prev || {}), [area]: params.api }));
	};

	const renderCategoryCell = (props: ICellRendererParams) => {
		if (!props.data) return null;

		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data.CraftColor || "#FFFFFF",
					}}
				>
					<div style={{ color: "#FFF", fontWeight: 600 }}>
						<div>{props.data.CraftName?.charAt(0) || ""}</div>
					</div>
				</div>
			</div>
		);
	};

	const renderTypeCell = (props: ICellRendererParams) => {
		if (!props.data) return null;

		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.TypeCode == "PM" ? "#00008B" : "#D3D3D3",
					}}
				>
					<div
						style={{
							color: props.data?.TypeCode == "PM" ? "#FFFFFF" : "#000000",
							fontWeight: 600,
						}}
					>
						<div>{props.data?.TypeCode || ""}</div>
					</div>
				</div>
			</div>
		);
	};

	const [columnDefs, setColumnDefs] = useState<ColDef[]>([
		{
			field: "",
			headerName: "",
			rowDrag: true,
			minWidth: 20,
			maxWidth: 20,
			suppressHeaderMenuButton: true,
			suppressHeaderFilterButton: true,
		},
		{
			cellRenderer: AssetNameColumnColumn,
			headerName: "Asset",
			minWidth: 90,
			flex: 4.5,
		},
		{ field: "Title", headerName: "Title", minWidth: 90, flex: 4.5 },
		{
			field: "DownHours",
			headerName: "Down Hours",
			minWidth: 60,
			maxWidth: 60,
			flex: 1,
		},
		{
			field: "CraftId",
			headerName: "Craft",
			minWidth: 35,
			maxWidth: 35,
			flex: 1,
			cellRenderer: renderCategoryCell,
		},
		{
			field: "TypeCode",
			headerName: "Type",
			minWidth: 55,
			maxWidth: 55,
			flex: 1,
			cellRenderer: renderTypeCell,
		},
		{
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: CellEditingStoppedEvent) => {
				return (
					<Grid
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid
							item
							xs={12}
							md={12}
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid item xs={12} md={12}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			},
		},
	]);

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const getContextMenuItems = useCallback(
		(
			params: GetContextMenuItemsParams,
			rootAssetId: number | null,
		): MenuItemDef[] => {
			if (!params.node?.data) return [];

			const status = params.node.data.WorkOrderStatusCode;
			const rootAssetIdFromParams = params.node.data.RootAssetId;

			if (params.node.data.WorkOrderId) {
				setWorkOrderSelectedId(params.node.data.WorkOrderId);
			}

			if (params.node.data.Title) {
				setWorkOrderSelectedName(params.node.data.Title);
			}

			const result: MenuItemDef[] = [];

			if (
				rootAssetIdFromParams === undefined ||
				rootAssetIdFromParams === null
			) {
				result.push({
					name: "New Order",
					action: () => {
						if (rootAssetId !== null) {
							addNewOrder(rootAssetId);
						}
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (rootAssetIdFromParams) {
				result.push({
					name: "Edit Order",
					action: () => {
						onMenuOptionSelected("EDIT", params.node?.data);
					},
					icon: '<i class="fas fa-edit"></i>',
				});

				if (status === "O") {
					result.push({
						name: "Close Order",
						action: () => {
							onMenuOptionSelected("CLOSE-ORDER", params.node?.data);
						},
						icon: '<i class="far fa-times-circle"></i>',
					});
				}

				result.push({
					name: "New Order",
					action: () => {
						if (rootAssetIdFromParams) {
							addNewOrder(rootAssetIdFromParams);
						}
					},
					icon: '<i class="fas fa-plus"></i>',
				});

				result.push({
					name: "Work Order Drawings",
					action: () => openResourceModal("DRAWINGS", params.node?.data),
					icon: '<i class="far fa-file-image"></i>',
				});

				result.push({
					name: "Work Order Attachments",
					action: () => openResourceModal("ATTACHMENTS", params.node?.data),
					icon: '<i class="fas fa-paperclip"></i>',
				});

				result.push({
					name: "Work Order LOTO",
					action: () => openResourceModal("LOTO", params.node?.data),
					icon: '<i class="fas fa-tag"></i>',
				});

				result.push({
					name: "Work Order Parts",
					action: () => openResourceModal("PARTS", params.node?.data),
					icon: '<i class="fas fa-tools"></i>',
				});

				result.push({
					name: "Delete Order",
					action: () => {
						onMenuOptionSelected("DELETE-ORDER", params.node?.data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			return result;
		},
		[rootAssetsDataTables, openResourceModal],
	);

	const onDoubleClick = (rootAsset: RowDoubleClickedEvent) => {
		const selectedRow = rootAsset?.api.getSelectedNodes()[0].data;
		onMenuOptionSelected("EDIT", selectedRow);
	};

	const resetFilter = () => {
		searchParams.delete("search");
		searchParams.delete("downHours");
		searchParams.delete("type");
		searchParams.delete("craft");
		setSearchParams(searchParams, { replace: true });
		setTimeout(() => {
			const currentPath = location.pathname;
			if (currentPath.includes("/downday")) {
				navigate(`/task-list/downday/${defaultAreaId}`, { replace: true });
			} else {
				navigate(`/task-list/area/${defaultAreaId}`, { replace: true });
			}
			setFirstTime(true);
		}, 500);
	};

	const changeAsset = (value: number | null) => {
		const currentPath = location.pathname;
		if (currentPath.includes("/downday")) {
			navigate(`/task-list/downday/${value}`, { replace: true });
		} else {
			navigate(`/task-list/area/${value}`, { replace: true });
		}
	};

	const addNewOrder = async (areaAssetId: number | string) => {
		if (!areaAssetId) return;

		const tempWorkOrder = {
			AssetId: areaAssetId,
			DownHours: downHours,
			WorkOrderId: null,
			Title: "",
			CraftId: 1,
			TypeCode: "RM",
			WorkOrderStatusCode: "O",
			WorkOrderName: "",
			WorkOrderNumberOfWorkers: null,
			OpenTimestamp: moment(new Date()).format("MM/DD/YYYY HH:mm"),
			WorkOrderPlannedHours: null,
			WorkOrderOpenedBy: getUserName(),
		};

		setNewMode(true);
		setSelectedRow(tempWorkOrder);
		setOpenWorkOrderEditModal(true);
	};

	const searchData = useSearchData({ setIsTaskListLoading, setTaskListError });
	const { GetCraftFromAPI } = useGetDataFromAPI();

	const mdBreakpoint = useMemo(() => {
		if (!rootAssetsDataTables) return 4;

		return rootAssetsDataTables.length === 1
			? 12
			: rootAssetsDataTables.length === 2
				? 6
				: 4;
	}, [rootAssetsDataTables]);

	useInitializeTaskList();

	useEffect(GetCraftFromAPI, []);

	const updateAttachmentsListFn = () => {
		if (selectedRow?.WorkOrderId) {
			searchWorkOrderAttachments();
		}
	};

	useEffect(() => {
		if (openWorkOrderEditModal && selectedRow?.WorkOrderId) {
			searchWorkOrderAttachments();
		}
	}, [openWorkOrderEditModal, updateAttachmentsList, selectedRow]);

	return (
		<>
			{openDialogLinkAttachmentToWo && selectedRow?.WorkOrderId && (
				<ViewerAttachmentsLinked
					show={openDialogLinkAttachmentToWo}
					workOrderSelectedId={selectedRow.WorkOrderId}
					workOrderSelectedName={selectedRow.Title}
					onHide={(shouldUpdate: boolean) => {
						if (shouldUpdate) {
							updateAttachmentsListFn();
						}
						setSelectedRow(INITIAL_VALUES_MODAL);
						setOpenDialogLinkAttachmentToWO(false);
					}}
				/>
			)}
			{openWorkOrderResources && selectedResource && selectedRow && (
				<TableWorkOrdesResoures
					show={openWorkOrderResources}
					onHide={(shouldUpdate: boolean) => {
						setOpenWorkOrderResources(false);
						setSelectedResource(null);
						if (shouldUpdate) {
							searchData();
						}
					}}
					AssetId={selectedRow.AssetId}
					WorkOrderId={selectedRow.WorkOrderId}
					initialResource={selectedResource}
					WorkOrderName={selectedRow.WorkOrderName as string}
					Title={selectedRow.Title as string}
					AssetName={selectedRow.AssetName as string}
					TypeCode={selectedRow.TypeCode as string}
					CraftId={selectedRow.CraftId as number}
					OpenTimestamp={selectedRow.OpenTimestamp}
					CloseTimestamp={selectedRow.CloseTimestamp}
				/>
			)}
			{openCloseWorkOrderModal && selectedRow && (
				<CloseWorkOrderModalWithAttachments
					show={openCloseWorkOrderModal}
					data={selectedRow}
					onHide={(shouldUpdate: boolean) => {
						if (shouldUpdate) {
							searchData();
							updateAttachmentsListFn();
						}
						setOpenCloseWorkOrderModal(false);
					}}
				/>
			)}
			<Grid
				container
				style={{ padding: 20 }}
				onContextMenu={(e) => e.preventDefault()}
			>
				<Grid item md={12} xs={12}>
					<Typography
						variant="h5"
						style={{ margin: "0", fontWeight: 600, userSelect: "none" }}
					>
						AREA TASK LIST
					</Typography>
				</Grid>
				<Paper
					elevation={1}
					style={{
						width: "100%",
						padding: 10,
						margin: "10px 0 20px",
						borderRadius: "0.75rem",
					}}
				>
					<Grid container alignItems="center" spacing={2}>
						<Grid container item lg={2} md={12} xs={12}>
							<Grid item md={12} xs={12} style={{ margin: "0 auto" }}>
								<AssetTreePicker
									label="Asset"
									value={Number.parseInt(assetSearchId as string) || 0}
									onChange={changeAsset}
								/>
							</Grid>
						</Grid>
						<Grid
							container
							item
							lg={7}
							md={12}
							xs={12}
							spacing={2}
							justifyContent="center"
							alignItems="center"
							style={{ margin: 0, padding: 0 }}
						>
							<Filters searchData={searchData} />
						</Grid>
						<Grid
							container
							item
							lg={3}
							md={12}
							xs={12}
							spacing={2}
							justifyContent="flex-end"
							alignItems="center"
							style={{ margin: "0 auto", padding: 0 }}
						>
							<Grid item lg={6} md={12} xs={12}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={resetFilter}
								>
									RESET
								</Button>
							</Grid>
							<Grid item lg={6} md={12} xs={12}>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									onClick={() => searchData()}
									type="submit"
								>
									SEARCH
								</Button>
							</Grid>
						</Grid>
					</Grid>
				</Paper>
				<Grid container spacing={2} style={{ marginTop: "5px" }}>
					{rootAssetsDataTables &&
						rootAssetsDataTables.map((rootAsset) => {
							if (!rootAsset || !rootAsset.rootAssetId) return null;

							return (
								<Grid
									key={rootAsset.rootAssetId}
									item
									md={mdBreakpoint}
									xs={12}
									style={{ height: "75vh", marginBottom: "20px" }}
								>
									<Paper
										elevation={2}
										style={{
											height: "75vh",
											width: "100%",
											padding: "10px 10px 0",
											borderRadius: "0.75rem",
										}}
									>
										<Grid
											container
											item
											md={12}
											xs={12}
											style={{
												height: "100%",
												width: "100%",
												position: "relative",
												paddingTop: 8,
											}}
										>
											<Grid item md={12} xs={12}>
												<Typography
													variant="h6"
													style={{
														margin: "0 0 0p 5px",
														fontWeight: 600,
														userSelect: "none",
													}}
												>
													{rootAsset.rootAssetName || ""}
												</Typography>
												<IconButton
													onClick={() => addNewOrder(rootAsset.rootAssetId)}
													aria-label="add"
													color="primary"
													style={{
														padding: 0,
														position: "absolute",
														top: "5px",
														right: "-5px",
													}}
												>
													<AddBoxIcon
														style={{
															width: 35,
															height: 35,
															marginRight: "0",
														}}
													/>
												</IconButton>
											</Grid>
											<Grid item style={gridStyle}>
												<AgGridReact
													rowSelection="single"
													onRowDoubleClicked={onDoubleClick}
													rowData={rootAsset.rows || []}
													columnDefs={columnDefs}
													defaultColDef={globalDefaultColDef}
													animateRows={true}
													getContextMenuItems={(params) =>
														getContextMenuItems(
															params,
															rootAsset.rootAssetId as number,
														)
													}
													className="ag-theme-mesf"
													headerHeight={32}
													rowHeight={20}
													getRowId={getRowId}
													rowDragEntireRow={true}
													onRowDragEnd={onRowDragEnd}
													onGridReady={(params) =>
														onGridReadyForArea(rootAsset.rootAssetId, params)
													}
													suppressMoveWhenRowDragging={false}
													loadingOverlayComponent={loadingOverlayComponent}
													loading={isTaskListLoading}
													rowDragText={(params) =>
														params.rowNode?.data?.Title || "Moving row"
													}
													suppressRowDrag={false}
												/>
											</Grid>
										</Grid>
									</Paper>
								</Grid>
							);
						})}
				</Grid>
				{openDeleteWorkOrderModal && selectedRow && (
					<DeleteWorkOrderModal
						show={openDeleteWorkOrderModal}
						data={selectedRow}
						onHide={(shouldUpdate: boolean) => {
							if (shouldUpdate) {
								searchData();
							}
							setOpenDeleteWorkOrderModal(false);
						}}
					/>
				)}
				{openWorkOrderEditModal && selectedRow && (
					<EditWorkOrderModal
						show={openWorkOrderEditModal}
						data={selectedRow}
						initialNewMode={newMode}
						onHide={(shouldUpdate: boolean) => {
							if (shouldUpdate) {
								searchData();
							}
							setNewMode(false);
							setOpenWorkOrderEditModal(false);
						}}
					/>
				)}
			</Grid>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal error={taskListError} onHide={() => setTaskListError("")} />
			<ErrorModal
				error={insertTaskError}
				onHide={() => setInsertTaskError("")}
			/>
			<ErrorModal
				error={updatePriorityError}
				onHide={() => setUpdatePriorityError("")}
			/>
		</>
	);
};
